import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';

import { UserStatusApiResponse } from 'routes/referral/types/user-status-api.types';
import { EventStatus } from 'routes/referral/types/event-detail-api.types';
import { EventType } from 'types/events/common.types';

import { queryKeys } from 'hooks/queries/query-keys';

import {
  getTradingEventDetailService,
  getTradingEventLeaderboardService,
  getTradingEventUserMetricsService,
} from 'services/trading-event.service';
import { getUserStatusService } from 'services/referral-event.service';

import { mapApiResponseToSingleEvent } from '../utils/mapTradingEventData';
import { ISingleEvent } from '../types';

type UseTradingEventInput = {
  eventId: string;
};

type UseTradingEventOutput = {
  event: ISingleEvent | null;
  loading: boolean;
  error: Error | null;
  userStatus: UserStatusApiResponse | undefined;
  eventStatus: EventStatus | undefined;
  eventType: EventType | undefined;
};

export const useTradingEvent = (input: UseTradingEventInput): UseTradingEventOutput => {
  const { eventId } = input;
  const walletAddress = useSelector((state: IState): string => state.wallet.address);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.event.detail(eventId),
    queryFn: async ({ signal }) => {
      // Fetch event detail and leaderboard in parallel
      const [eventDetailResponse, leaderboardResponse] = await Promise.all([
        getTradingEventDetailService(eventId, signal),
        getTradingEventLeaderboardService(eventId, { page: 1, page_size: 100 }, signal),
      ]);

      // Fetch user metrics only if wallet is connected
      let userMetricsResponse = null;
      if (walletAddress) {
        try {
          userMetricsResponse = await getTradingEventUserMetricsService(eventId, signal);
        } catch {
          // User metrics may fail if user is not participating
          console.warn('Failed to fetch user metrics - user may not be participating');
        }
      }

      const eventDetail = eventDetailResponse.data;
      const leaderboard = leaderboardResponse.data;
      const userMetrics = userMetricsResponse?.data || null;

      return {
        event: mapApiResponseToSingleEvent(eventDetail, leaderboard, userMetrics),
        eventStatus: eventDetail.status as EventStatus,
        eventType: eventDetail.event_type,
      };
    },
    enabled: !!eventId,
  });

  // Fetch user status separately (for registration state)
  const { data: userStatus } = useQuery({
    queryKey: queryKeys.event.userStatus(eventId),
    queryFn: async ({ signal }) => {
      const response = await getUserStatusService(eventId, signal);
      return response.data;
    },
    enabled: !!eventId && !!walletAddress,
  });

  return {
    event: data?.event || null,
    loading: isLoading,
    error,
    userStatus,
    eventStatus: data?.eventStatus,
    eventType: data?.eventType,
  };
};
